/**
 * perUnit (#2844) — per-unit economics derived from ONE deckungsbeitrag fact,
 * for ANY Bezugsgröße (#2304's ``MarginBasis``), not only the kilogram. The fact
 * stores season totals; this module divides them by one chosen denominator so the
 * drawer can answer "lohnt sich je Kilo?" AND "lohnt sich je Pflanze?" — the
 * latter being the unit a berry grower actually plants, prices and replaces.
 *
 * This module is the generalisation of the former kg-only ``perKg`` module, and
 * it exists to protect exactly two invariants that a naive re-implementation
 * loses. Both are carried over VERBATIM:
 *
 * 1. ``costPerUnit`` is ``(leistung − db1) / q`` — the ENGINE'S COMPLEMENT:
 *    everything the materialiser subtracted between Leistung and DB1 (direct
 *    costs + amortised establishment + allocated overhead). It is NEVER a
 *    re-summed bucket list, which could drift from the engine's own rounding.
 * 2. ``dbPerUnit`` is reconstructed as ``revenuePerUnit − costPerUnit`` AFTER
 *    both are rounded (#2188), so the DISPLAYED identity
 *    ``Ø-Erlös − Kosten === DB`` always holds to the cent. Three independent
 *    ``round2()`` calls drift a cent at rounding boundaries.
 *
 * All math is null-honest (#2844 criterion 6, and on WF today the NORMAL path):
 * an absent, zero or non-finite denominator yields null so the caller renders NO
 * panel — never a fabricated "0,00 €/Pflanze", never a divide-by-zero.
 */
import { basisQuantity, type MarginBasis } from './basis';
import { classifyRow } from './rowQuality';
import type { DeckungsbeitragRow } from './types';

export interface PerUnitEconomics {
  /** The denominator divided by, in the unit the panel names (never <= 0). */
  quantity: number;
  /** Ø-Erlös je Einheit = leistung / q. Null in the revenue-pending state (#2168). */
  revenuePerUnit: number | null;
  /** Kosten je Einheit = (leistung − db1) / q — exact complement of dbPerUnit. Always real. */
  costPerUnit: number;
  /** Deckungsbeitrag je Einheit = revenuePerUnit − costPerUnit, reconstructed after rounding so the displayed identity holds to the cent (#2188). Null in the revenue-pending state (#2168). */
  dbPerUnit: number | null;
  /** True when the unit covers its costs (dbPerUnit >= 0). False while revenue is pending. */
  coversCosts: boolean;
  /**
   * #2168 — the quantity exists but no revenue is booked yet (the classifier's ``pending``
   * state: costs pushed db1 below a zero Leistung). Quantity + Kosten are real; Ø-Erlös and
   * DB render "—" rather than a misleading 0,00 €/Einheit and a red "unter der Kostenlinie"
   * verdict.
   */
  revenuePending: boolean;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The DRAWER's denominator for ``basis`` on one row.
 *
 * ⚠️ ``kg`` deliberately does NOT go through ``basisQuantity``. That function
 * normalises an arable ``dt`` yield ×100 because the RANKING compares €/kg across
 * crops; the drawer's Je-Einheit panel instead reads in the fact's OWN declared
 * unit and titles itself "Je Dezitonne" accordingly. Routing the panel through the
 * normalised quantity would silently relabel 5,00 €/dt as 0,05 €/dt under an
 * unchanged heading. Every other basis has one unambiguous quantity, so it defers.
 */
export function perUnitQuantity(row: DeckungsbeitragRow, basis: MarginBasis): number | null {
  if (basis !== 'kg') return basisQuantity(row, basis);
  return Number.isFinite(row.yield) && row.yield > 0 ? row.yield : null;
}

/**
 * Per-unit economics over an explicit denominator. Null when the denominator is
 * absent, zero or non-finite — a per-unit figure over an unknown quantity would be
 * fabricated precision.
 *
 * With a real quantity but no revenue yet (#2168) returns the revenue-pending state:
 * Kosten je Einheit is exact without revenue (``-db1 / q``), while Ø-Erlös and DB are
 * null. The revenueless heuristic is the shared row classifier's ``pending`` state
 * (``rowQuality.ts``, #2773 A — today exactly #2159's ``leistung === 0 &&
 * leistung − db1 > 0``) so the panel stays in lock-step with the chart's gray
 * "noch keine Erlöse gebucht" bars.
 */
export function perUnitEconomics(
  row: DeckungsbeitragRow,
  quantity: number | null | undefined,
): PerUnitEconomics | null {
  if (quantity == null || !Number.isFinite(quantity) || quantity <= 0) return null;
  const costPerUnit = round2((row.leistung - row.db1) / quantity);
  if (classifyRow(row).state === 'pending') {
    return {
      quantity,
      revenuePerUnit: null,
      costPerUnit,
      dbPerUnit: null,
      coversCosts: false,
      revenuePending: true,
    };
  }
  const revenuePerUnit = round2(row.leistung / quantity);
  // #2188 — reconstruct DB je Einheit from the two already-rounded figures rather than
  // an independent round2(db1 / q), so the displayed margin is self-consistent to the
  // cent (Ø-Erlös − Kosten === DB) at every rounding boundary, for every basis.
  const dbPerUnit = round2(revenuePerUnit - costPerUnit);
  return {
    quantity,
    revenuePerUnit,
    costPerUnit,
    dbPerUnit,
    coversCosts: dbPerUnit >= 0,
    revenuePending: false,
  };
}

/**
 * Per-unit economics for one ``MarginBasis`` — the drawer's entry point. Null when
 * the fact carries no denominator for that basis, which on WF today is the normal
 * answer for ``plant``: only 22 of 474 Beeren/Obst crop_cycles (4,6 %) carry a
 * ``plant_count`` until #2807 fills the density block.
 */
export function perBasisEconomics(row: DeckungsbeitragRow, basis: MarginBasis): PerUnitEconomics | null {
  return perUnitEconomics(row, perUnitQuantity(row, basis));
}
